/**
 * RBAC-4.4 · What one person can actually do, and where each permission comes from (FR-17, UX-DR14, NFR-8).
 *
 * Pure, no DOM, no Supabase. The rows come from `person_effective_permissions()`
 * (`20261102a7_rbac_4_4_person_effective_permissions.sql`) via
 * `services/personEffectivePermissionsService.ts`. The screen is
 * `components/settings/components/users/EffectivePermissionsSection.tsx`, inside RBAC-4.1's PersonRoleCard.
 *
 * ⚠️ THE SET IS THE AUTHORITY FUNCTION'S, NOT `users.role_id`'S (AC 3). Each row's `effective` is
 * `cost_holds_permission_for()` called by the RPC, which since RBAC-7.1 delegates to
 * `holds_permission_for()`: a role held in another account counts only if it is the global
 * Superadmin. The RPC still lists such roles, so `countsHere()` keeps them out of the origins.
 *
 * ⚠️ "CHANGING THEIR ROLE WILL NOT REMOVE IT" IS TRUE ONLY OF SOME ORIGINS. RBAC-4.1's
 * `assign_person_role()` replaces the person's own extra roles in this account along with their role,
 * and keeps parent-account and other-account rows. So a permission that comes only from an extra role
 * in this account DOES go with a role change, and the screen says that instead (NFR-13).
 */

import { PERMISSION_AREAS, permissionRow, rendersInEditor, type PermissionArea } from './permissionDisplay';
import type { RoleOrigin } from './personRole';
import { listOf, permissionTitle } from './rolePermissionDiff';

export const PERSON_EFFECTIVE_PERMISSIONS_RPC = 'person_effective_permissions';

export interface EffectiveRole {
  roleId: number;
  name: string;
  origin: RoleOrigin;
  parentAccountName: string | null;
  carriesOverride: boolean;
}

export interface EffectivePermission {
  key: string;
  /** Roles the person holds that grant it directly. Empty when it is held only by override. */
  roleIds: number[];
  byOverride: boolean;
  /** `cost_holds_permission_for(person, key)`: false for everything while their access is removed. */
  effective: boolean;
}

export interface PersonEffectivePermissions {
  userId: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  primaryRoleId: number | null;
  roles: EffectiveRole[];
  override: boolean;
  permissions: EffectivePermission[];
}

const ORIGINS: readonly RoleOrigin[] = ['primary', 'account', 'parent_account', 'other_account'];

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** The RPC's jsonb → view model. Anything it does not recognise is null, never a guess. */
export function parsePersonEffectivePermissions(raw: unknown): PersonEffectivePermissions | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.user_id !== 'string' || !Array.isArray(r.roles) || !Array.isArray(r.permissions)) return null;
  const roles: EffectiveRole[] = (r.roles as Record<string, unknown>[])
    .map((e) => ({
      roleId: num(e?.role_id),
      name: String(e?.name ?? ''),
      origin: e?.origin as RoleOrigin,
      parentAccountName: typeof e?.parent_account_name === 'string' ? e.parent_account_name : null,
      carriesOverride: e?.carries_override === true,
    }))
    .filter((e): e is EffectiveRole => e.roleId !== null && ORIGINS.includes(e.origin));
  const permissions: EffectivePermission[] = (r.permissions as Record<string, unknown>[])
    .filter((e) => typeof e?.key === 'string')
    .map((e) => ({
      key: e.key as string,
      roleIds: (Array.isArray(e.role_ids) ? e.role_ids : []).map(num).filter((n): n is number => n !== null),
      byOverride: e.by_override === true,
      effective: e.effective === true,
    }));
  return {
    userId: r.user_id,
    firstName: typeof r.first_name === 'string' ? r.first_name : '',
    lastName: typeof r.last_name === 'string' ? r.last_name : '',
    isActive: r.is_active !== false,
    primaryRoleId: num(r.primary_role_id),
    roles,
    override: r.override === true,
    permissions,
  };
}

export interface EffectiveItem {
  key: string;
  title: string;
  description: string;
  /** Every route the person holds it by, in words. Never a key. */
  origins: string[];
  /** Only on items that did not come with their role: what changing their role would do to it. */
  roleChange: string | null;
}

export interface EffectiveArea {
  area: PermissionArea;
  items: EffectiveItem[];
}

export interface EffectivePermissionsView {
  summary: string;
  inactiveNotice: string | null;
  overrideNotice: string | null;
  /** AC 2: what did not come with their role, listed first and set apart. */
  elsewhereHeading: string | null;
  elsewhere: EffectiveItem[];
  fromRoleHeading: string | null;
  fromRole: EffectiveArea[];
  /** Hidden and reader-pending permissions they hold, counted, never listed by key. */
  unlisted: string | null;
}

/**
 * RBAC-7.1: `holds_permission_for()` honours a role held in another account only when it is the
 * global Superadmin (the role that carries override). Any other such role is listed by the RPC but
 * grants nothing here, so it is never named as an origin.
 */
const countsHere = (r: EffectiveRole): boolean => r.origin !== 'other_account' || r.carriesOverride;

/** Survive `assign_person_role()`: it replaces their role and their own extra roles here, nothing else. */
const SURVIVES: ReadonlySet<RoleOrigin> = new Set(['parent_account', 'other_account']);

function roleOrigin(r: EffectiveRole): string {
  switch (r.origin) {
    case 'primary':
      return `Their role, ${r.name}`;
    case 'account':
      return `${r.name}, an extra role in this account`;
    case 'parent_account':
      return `${r.name}, from ${r.parentAccountName?.trim() || 'a parent account'}, where they are an Agency Admin`;
    case 'other_account':
      return `${r.name}, a role they hold in another account`;
  }
}

const areaRank = (key: string) => {
  const a = permissionRow(key)?.area;
  const i = a ? PERMISSION_AREAS.indexOf(a) : -1;
  return i < 0 ? PERMISSION_AREAS.length : i;
};

const things = (n: number) => (n === 1 ? '1 thing' : `${n} things`);

/** AC 1-3 in words: every permission with its origin, what is not from their role first. */
export function effectivePermissionsView(p: PersonEffectivePermissions): EffectivePermissionsView {
  const name = p.firstName.trim() || 'This person';
  const primary = p.roles.find((r) => r.origin === 'primary') ?? null;
  const overrideRoles = p.roles.filter((r) => r.carriesOverride);
  // Override that rides on their own role is their role's, like anything else it carries.
  const overrideViaPrimary = overrideRoles.some((r) => r.origin === 'primary');
  const overrideSurvives = overrideRoles.some((r) => SURVIVES.has(r.origin));

  // RBAC-7.1: roles that count in this account. The RPC lists every role, so this is where the rule
  // the database applies is applied to the words.
  const countingIds = new Set(p.roles.filter(countsHere).map((r) => r.roleId));

  // Active: what the authority function grants. Removed: what restoring would give back.
  const held = p.permissions.filter((x) =>
    p.isActive ? x.effective : x.byOverride || x.roleIds.some((id) => countingIds.has(id)),
  );
  const shown = held.filter((x) => rendersInEditor(x.key));

  const items = shown
    .sort((a, b) => areaRank(a.key) - areaRank(b.key) || permissionTitle(a.key).localeCompare(permissionTitle(b.key)))
    .map((x) => {
      const via = p.roles.filter((r) => x.roleIds.includes(r.roleId) && countsHere(r));
      // A role can be listed under two origins (their role here and an extra row elsewhere): one line each.
      const origins = via.map(roleOrigin);
      const byOverrideOnly = x.byOverride && via.length === 0;
      if (byOverrideOnly) {
        origins.push(
          overrideViaPrimary
            ? `Superadmin override, through their role, ${primary?.name ?? ''}`.trimEnd()
            : `Superadmin override, through ${listOf(overrideRoles.map((r) => r.name))}`,
        );
      }
      const fromRole = via.some((r) => r.origin === 'primary') || (byOverrideOnly && overrideViaPrimary);
      let roleChange: string | null = null;
      if (!fromRole) {
        const survives = via.some((r) => SURVIVES.has(r.origin)) || (byOverrideOnly && overrideSurvives);
        const replaced = [...new Set(via.filter((r) => r.origin === 'account').map((r) => r.name))];
        roleChange = survives || replaced.length === 0
          ? 'Changing their role will not remove this.'
          : `Changing their role here replaces ${listOf(replaced)} too, so this goes with it.`;
      }
      const row = permissionRow(x.key);
      return { fromRole, item: { key: x.key, title: permissionTitle(x.key), description: row?.description ?? '', origins, roleChange } };
    });

  const elsewhere = items.filter((i) => !i.fromRole).map((i) => i.item);
  const mine = items.filter((i) => i.fromRole).map((i) => i.item);
  const fromRole: EffectiveArea[] = PERMISSION_AREAS.map((area) => ({
    area,
    items: mine.filter((i) => permissionRow(i.key)?.area === area),
  })).filter((a) => a.items.length > 0);

  const n = shown.length;
  let summary: string;
  if (n === 0) summary = p.isActive ? `${name} can't do anything this screen lists.` : `${name} would not be able to do anything this screen lists.`;
  else if (!p.isActive) summary = `${name} would be able to do ${things(n)} this screen lists.`;
  else {
    summary = `${name} can do ${things(n)} this screen lists.`;
    if (elsewhere.length > 0) {
      summary += elsewhere.length === 1 ? ' 1 of them does not come from their role.' : ` ${elsewhere.length} of them do not come from their role.`;
    }
  }

  const hidden = held.length - shown.length;
  return {
    summary,
    inactiveNotice: p.isActive
      ? null
      : `${name}'s access has been removed, so they can do none of this now. Restoring their access gives back what is listed.`,
    overrideNotice: p.override
      ? `${name} holds superadmin override through ${listOf(overrideRoles.map((r) => r.name))}, so the permission checks let them do everything, whatever their role carries.`
      : null,
    elsewhereHeading:
      elsewhere.length === 0 ? null : primary ? `Not from their role. ${primary.name} doesn't give these.` : "Not from their role. They don't have one.",
    elsewhere,
    fromRoleHeading: primary && fromRole.length > 0 ? `From their role, ${primary.name}` : null,
    fromRole,
    unlisted: hidden <= 0 ? null : hidden === 1 ? 'Plus 1 more permission this screen does not list.' : `Plus ${hidden} more permissions this screen does not list.`,
  };
}
